import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';

import { showSnackbar } from 'helpers/snackbar';

import { media } from 'context';
import { Layout, Modal, PillButton, Text } from 'ds';
import { CONSUMABLES_CHECKOUT_PATH } from 'routes/paths';
import { ConsumablesProductSubscription } from 'shared';
import { actions } from 'store/Consumables';
import { selectProductSubscriptions } from 'store/Consumables/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import ConsumablesOnboardingCartItemCard from './ConsumablesOnboardingCartItemCard';
import ConsumablesOnboardingRestockingInfoMessage from './ConsumablesOnboardingRestockingInfoMessage';
import { setUpProductSubscriptions } from '../requests';

interface ConsumablesOnboardingCartModalProps {
  isVisible: boolean;
  onClose: () => void;
}

const ConsumablesOnboardingCartModal: React.FC<ConsumablesOnboardingCartModalProps> = ({ isVisible, onClose }) => {
  const { isMobile } = useContext(media);
  const history = useHistory();
  const productSubscriptions = useAppSelector(selectProductSubscriptions);

  const dispatch = useAppDispatch();
  const handleIncreaseQuantity = async (productSubscription: ConsumablesProductSubscription) => {
    try {
      const response = await setUpProductSubscriptions([
        {
          consumables_product_id: productSubscription.consumables_product.id,
          quantity: productSubscription.order_quantity + 1
        }
      ]);
      if (response.status === 200) {
        dispatch(actions.setConsumablesProductSubscriptions(response.data.consumables_product_subscriptions));
      } else {
        showSnackbar({
          message: 'Failed to increase quantity. Please refresh and try again.',
          negative: true
        });
      }
    } catch (error) {
      console.error(error);
      showSnackbar({
        message: 'Failed to increase quantity. Please refresh and try again.',
        negative: true
      });
    }
  };

  const handleDecreaseQuantity = async (productSubscription: ConsumablesProductSubscription) => {
    try {
      const response = await setUpProductSubscriptions([
        {
          consumables_product_id: productSubscription.consumables_product.id,
          quantity: productSubscription.order_quantity - 1
        }
      ]);
      if (response.status === 200) {
        dispatch(actions.setConsumablesProductSubscriptions(response.data.consumables_product_subscriptions));
      } else {
        showSnackbar({
          message: 'Failed to decrease quantity. Please refresh and try again.',
          negative: true
        });
      }
    } catch (error) {
      console.error(error);
      showSnackbar({
        message: 'Failed to decrease quantity. Please refresh and try again.',
        negative: true
      });
    }
  };

  return (
    <Modal
      header="Your pantry cart"
      headerAlign={isMobile ? 'center' : 'left'}
      isSideDrawer
      onClose={onClose}
      paddingX={24}
      isVisible={isVisible}
      width={422}
      paddingTop={0}
      footer={
        productSubscriptions &&
        productSubscriptions.length > 0 &&
        !isMobile && (
          <Layout>
            <PillButton
              text="Go to checkout"
              size="lg"
              type="purpleGradient"
              borderRadius={40}
              fullWidth
              onClick={() => {
                history.push(CONSUMABLES_CHECKOUT_PATH);
              }}
            />
          </Layout>
        )
      }
      overflow="visible"
      showSideDrawerScrim
      isBottomSheet={isMobile}
      bottomSheetIsRounded={isMobile}
    >
      <Layout
        flex
        direction="column"
        gap={isMobile ? 24 : 36}
        marginTop={24}
        paddingX={isMobile ? 24 : undefined}
        paddingBottom={isMobile ? 150 : undefined}
      >
        {productSubscriptions && productSubscriptions.length > 0 ? (
          <>
            <ConsumablesOnboardingRestockingInfoMessage />
            <Layout flex direction="column" gap={24}>
              {productSubscriptions.map(productSubscription => (
                <ConsumablesOnboardingCartItemCard
                  key={productSubscription.id}
                  consumablesProductSubscription={productSubscription}
                  handleDecreaseQuantity={() => handleDecreaseQuantity(productSubscription)}
                  handleIncreaseQuantity={() => handleIncreaseQuantity(productSubscription)}
                />
              ))}
            </Layout>
          </>
        ) : (
          <Layout paddingX={24}>
            <Text size="body-sm" color="gray-400">
              Your cart is empty.
            </Text>
          </Layout>
        )}
      </Layout>
      {isMobile && productSubscriptions && productSubscriptions.length > 0 && (
        <Layout position="fixed" bottom={20} left={0} right={0} paddingX={24}>
          <PillButton
            text="Go to checkout"
            size="lg"
            type="purpleGradient"
            borderRadius={40}
            fullWidth
            onClick={() => {
              history.push(CONSUMABLES_CHECKOUT_PATH);
            }}
          />
        </Layout>
      )}
    </Modal>
  );
};

export default ConsumablesOnboardingCartModal;
